import React from 'react';
import {
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography
} from '@material-ui/core/';

/**
 * The `table` result format — one or more labelled tables of flat rows.
 *
 *     tables: [ { title, note?, columns?: [{ key, label }], rows: [ {…} ] } ]
 *
 * **`columns` is optional on purpose.** Any list of flat objects is table-shaped, and requiring an algorithm
 * to restate its own keys before a table would draw is the sort of ceremony that stops people emitting the
 * block at all. Absent, the columns are the union of the keys present across the rows — the union rather than
 * the first row's keys, because a row that happens to omit an optional field would otherwise hide a column
 * for every other row too.
 */

const prettify = (key) => String(key)
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase());

const columnsFor = (table) => {
    if (Array.isArray(table.columns) && table.columns.length) {
        return table.columns.map((c) => (typeof c === 'string'
            ? { key: c, label: prettify(c) }
            : { key: c.key, label: c.label || prettify(c.key) }));
    }
    const keys = [];
    for (const row of (table.rows || [])) {
        if (row && typeof row === 'object') {
            for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
        }
    }
    return keys.map((k) => ({ key: k, label: prettify(k) }));
};

// Numbers get 4 decimals and right alignment; everything else is left alone and left aligned.
const isNumber = (v) => typeof v === 'number' && isFinite(v);
const format = (v) => {
    if (v === null || v === undefined || v === '') return '';
    if (isNumber(v)) return Number.isInteger(v) ? String(v) : v.toFixed(4);
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

function OneTable({ table, index }) {
    const columns = columnsFor(table);
    const rows = Array.isArray(table.rows) ? table.rows : [];
    const [sort, setSort] = React.useState({ key: null, asc: false });

    const sorted = React.useMemo(() => {
        if (!sort.key) return rows;
        const dir = sort.asc ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a && a[sort.key];
            const y = b && b[sort.key];
            if (isNumber(x) && isNumber(y)) return (x - y) * dir;
            return String(x === undefined ? '' : x).localeCompare(String(y === undefined ? '' : y)) * dir;
        });
    }, [rows, sort]);

    if (!columns.length || !rows.length) return null;

    return <Paper style={{ marginBottom: 20, padding: 10, overflowX: 'auto' }}>
        <Typography variant="subtitle1">{table.title || `Table ${index + 1}`}</Typography>
        {table.note ? <Typography variant="caption" style={{ opacity: 0.75 }}>{table.note}</Typography> : null}
        <Table size="small">
            <TableHead>
                <TableRow>
                    {columns.map((c) => <TableCell key={c.key}
                        align={isNumber(rows[0] && rows[0][c.key]) ? 'right' : 'left'}>
                        <TableSortLabel
                            active={sort.key === c.key}
                            direction={sort.asc ? 'asc' : 'desc'}
                            onClick={() => setSort({ key: c.key, asc: sort.key === c.key ? !sort.asc : false })}>
                            {c.label}
                        </TableSortLabel>
                    </TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {sorted.map((row, r) => <TableRow key={r}>
                    {columns.map((c) => <TableCell key={c.key}
                        align={isNumber(row && row[c.key]) ? 'right' : 'left'}>
                        {format(row && row[c.key])}
                    </TableCell>)}
                </TableRow>)}
            </TableBody>
        </Table>
    </Paper>;
}

export default function ResultTables({ data }) {
    const tables = Array.isArray(data && data.tables) ? data.tables : [];
    const drawn = tables.map((t, i) => <OneTable key={i} table={t} index={i} />).filter(Boolean);

    if (!drawn.length) {
        return <Typography variant="body2" style={{ padding: 20, opacity: 0.7 }}>
            This result has no <code>tables</code> block with rows in it.
        </Typography>;
    }

    return <div>
        {drawn}
        {Array.isArray(data.warnings) && data.warnings.length
            ? <Paper style={{ padding: 10 }}>
                <Typography variant="subtitle2">Warnings</Typography>
                {data.warnings.map((w, i) => <Typography key={i} variant="body2">• {w}</Typography>)}
            </Paper>
            : null}
    </div>;
}
